import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Ctx } from '../ctx';
import { ErrorBanner, Pill } from '../ui';
import { assessmentPc, type AssessmentPc } from './assessmentInventory';

type Kind = 'website' | 'application' | 'server' | 'network' | 'device' | 'other';
type Status = 'candidate' | 'confirmed' | 'excluded';
interface Asset {
  id: number; name: string; kind: Kind; address: string | null; owner: string | null;
  status: Status; notes: string | null;
}
type Form = Omit<Asset, 'id'>;
const empty: Form = { name: '', kind: 'website', address: null, owner: null, status: 'candidate', notes: null };
const kinds: Record<Kind, string> = {
  website: 'Sito web', application: 'Software', server: 'Server', network: 'Rete', device: 'Dispositivo', other: 'Altro',
};
const statuses: Record<Status, string> = {
  candidate: 'Da verificare', confirmed: 'Confermato dal cliente', excluded: 'Fuori dal servizio',
};

export function SecurityAssets({ ctx, siteId }: { ctx: Ctx; siteId: number }) {
  const base = `/api/guard/sites/${siteId}/assets`;
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foundPcs, setFoundPcs] = useState<AssessmentPc[] | null>(null);
  const reportInput = useRef<HTMLInputElement>(null);
  const load = useCallback(async () => {
    try { setAssets(await ctx.api.get<Asset[]>(base)); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, [base, ctx.api]);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (editing === null || !form.name.trim()) return;
    setBusy(true); setError(null);
    try {
      const input: Form = { ...form, name: form.name.trim(),
        address: form.address?.trim() || null, owner: form.owner?.trim() || null, notes: form.notes?.trim() || null };
      if (editing === 'new') await ctx.api.post(base, input);
      else await ctx.api.put(`${base}/${editing}`, input);
      setEditing(null); setForm(empty); await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const readReports = async (files: FileList | null) => {
    if (!files) return;
    setError(null);
    const reports = Array.from(files).filter((file) => file.name.toLowerCase() === 'report.json');
    if (!reports.length || reports.length > 200) {
      setError('Scegli la cartella dei report Assessment (fino a 200 PC).');
      return;
    }
    setBusy(true);
    try {
      const byName = new Map<string, AssessmentPc>();
      for (const file of reports) {
        if (file.size > 5_000_000) continue;
        try {
          const pc = assessmentPc(JSON.parse(await file.text()));
          if (pc && (!byName.has(pc.name) || (pc.checkedAt ?? '') > (byName.get(pc.name)?.checkedAt ?? ''))) byName.set(pc.name, pc);
        } catch { /* Un file non valido non blocca gli altri. */ }
      }
      const pcs = [...byName.values()].sort((a, b) => a.name.localeCompare(b.name, 'it'));
      if (!pcs.length) throw new Error('Nessun report Assessment valido nella cartella scelta.');
      setFoundPcs(pcs);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); if (reportInput.current) reportInput.current.value = ''; }
  };

  const newPcs = assets && foundPcs ? foundPcs.filter((pc) => !assets.some((asset) => asset.kind === 'device' && (asset.name.toUpperCase() === pc.name || asset.address?.toUpperCase() === pc.name))) : [];
  const importPcs = async () => {
    if (!newPcs.length) return;
    setBusy(true); setError(null);
    let saved = 0;
    let failure: string | null = null;
    try {
      for (const pc of newPcs) {
        const date = pc.checkedAt ? new Date(pc.checkedAt).toLocaleDateString('it-IT') : 'data non disponibile';
        await ctx.api.post(base, { name: pc.name, kind: 'device', address: pc.name, owner: null, status: 'candidate', notes: `Report Assessment del ${date}: CrowdStrike ${pc.crowdStrike}. Da confermare con il tecnico del cliente.` });
        saved++;
      }
      setFoundPcs(null);
    } catch (e) { failure = `${saved} PC importati; poi il caricamento si è fermato: ${(e as Error).message}`; }
    finally { await load(); if (failure) setError(failure); setBusy(false); }
  };

  return <section style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 18, margin: '20px 0', background: 'var(--white)' }}>
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
      <h3 className="guard-h" style={{ margin: 0 }}>Sistemi del cliente</h3>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input ref={reportInput} type="file" accept=".json,application/json" multiple style={{ display: 'none' }} onChange={(event) => void readReports(event.target.files)} />
        <button type="button" className="btn small" disabled={busy} onClick={() => { reportInput.current?.setAttribute('webkitdirectory', ''); reportInput.current?.click(); }}>Importa report dei PC</button>
        <button type="button" className="btn small" onClick={() => { setForm(empty); setEditing('new'); }}>Aggiungi sistema</button>
      </div>
    </div>
    <p className="muted small" style={{ margin: '8px 0 14px' }}>Siti, software, server, reti e PC da seguire. Importare i report non avvia controlli; salva solo nomi, date ed esito di CrowdStrike.</p>
    <ErrorBanner message={error} onClose={() => setError(null)} />
    {foundPcs && <div style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
      <b>{foundPcs.length} PC letti · {newPcs.length} da aggiungere</b>
      <div className="muted small" style={{ margin: '6px 0 10px' }}>{foundPcs.map((pc) => `${pc.name}: CrowdStrike ${pc.crowdStrike}`).join(' · ')}</div>
      <button type="button" className="btn dark small" disabled={busy || !newPcs.length} onClick={() => void importPcs()}>{busy ? 'Importazione…' : `Aggiungi ${newPcs.length} PC`}</button>
      <button type="button" className="btn ghost small" disabled={busy} onClick={() => setFoundPcs(null)}>Annulla</button>
    </div>}
    {assets?.length === 0 && <p className="small">Nessun sistema registrato.</p>}
    {assets?.map((asset) => <div key={asset.id} style={{ display: 'flex', gap: 10, alignItems: 'center', borderTop: '1px solid var(--line)', padding: '12px 0' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b>{asset.name}</b>
        <div className="muted small">{kinds[asset.kind]}{asset.address ? ` · ${asset.address}` : ''}{asset.owner ? ` · ${asset.owner}` : ''}</div>
        {asset.notes && <div className="muted small">{asset.notes}</div>}
      </div>
      <Pill tone={asset.status === 'confirmed' ? 'good' : asset.status === 'excluded' ? 'soft' : 'warn'}>{statuses[asset.status]}</Pill>
      <button type="button" className="btn ghost small" onClick={() => { setForm({ name: asset.name, kind: asset.kind, address: asset.address, owner: asset.owner, status: asset.status, notes: asset.notes }); setEditing(asset.id); }}>Modifica</button>
    </div>)}
    {editing !== null && <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, display: 'grid', gap: 10, maxWidth: 640 }}>
      <b>{editing === 'new' ? 'Nuovo sistema' : 'Modifica sistema'}</b>
      <label>Nome<br /><input value={form.name} maxLength={160} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ width: '100%' }} /></label>
      <label>Tipo<br /><select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as Kind })}>{Object.entries(kinds).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Indirizzo o nome nella rete<br /><input value={form.address ?? ''} maxLength={500} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Es. sito.it o SERVER-DDT" style={{ width: '100%' }} /></label>
      <label>Chi lo gestisce<br /><input value={form.owner ?? ''} maxLength={160} onChange={(e) => setForm({ ...form, owner: e.target.value })} placeholder="Es. tecnico del cliente" style={{ width: '100%' }} /></label>
      <label>Stato<br /><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Status })}>{Object.entries(statuses).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
      <label>Note<br /><textarea rows={2} value={form.notes ?? ''} maxLength={4000} onChange={(e) => setForm({ ...form, notes: e.target.value })} style={{ width: '100%' }} /></label>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" className="btn dark small" disabled={busy || form.name.trim().length < 2} onClick={() => void save()}>Salva sistema</button>
        <button type="button" className="btn ghost small" onClick={() => setEditing(null)}>Annulla</button>
      </div>
    </div>}
  </section>;
}
